import type { EventConfig } from '@/types'
import { PAPER_SIZES } from './constants'

export async function generateLayoutImage(event: EventConfig, photos: string[]): Promise<string | null> {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  const layout = event.printLayout
  
  // Determine correct paper size
  let canvasW = 1200
  let canvasH = 1800
  if (layout.paperSize === 'custom') {
    canvasW = layout.customWidth
    canvasH = layout.customHeight
  } else if (PAPER_SIZES[layout.paperSize]) {
    canvasW = PAPER_SIZES[layout.paperSize].width
    canvasH = PAPER_SIZES[layout.paperSize].height
  }
  
  canvas.width = canvasW
  canvas.height = canvasH
  
  // 1. Draw background color
  ctx.fillStyle = layout.backgroundColor || '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  // 2. Prepare all drawing tasks in order (z-index)
  const drawTasks: { src: string; draw: (img: HTMLImageElement) => void }[] = []

  // Layer 1: Background Image
  if (layout.backgroundImage) {
    drawTasks.push({
      src: layout.backgroundImage,
      draw: (img) => ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    })
  }

  // Layer 2: Slots (sorted by zIndex)
  const sortedSlots = [...layout.slots].sort((a, b) => a.zIndex - b.zIndex)
  for (const slot of sortedSlots) {
    if (slot.type === 'photo') {
      const idx = slot.photoIndex ?? 0
      if (photos[idx]) {
        drawTasks.push({
          src: photos[idx],
          draw: (img) => ctx.drawImage(img, slot.x, slot.y, slot.width, slot.height)
        })
      }
    }
  }

  // Layer 3: Overlay Image (Foreground)
  if (layout.overlayImage) {
    drawTasks.push({
      src: layout.overlayImage,
      draw: (img) => ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    })
  }

  if (drawTasks.length === 0) {
    return canvas.toDataURL('image/jpeg', 0.92)
  }

  // Helper to load an image as a promise
  const loadImage = (src: string): Promise<HTMLImageElement | null> => {
    return new Promise((resolve) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => resolve(img)
      img.onerror = () => resolve(null) // Skip on error without failing
      img.src = src
    })
  }

  // 3. Load all images in parallel
  const loadedImages = await Promise.all(drawTasks.map(t => loadImage(t.src)))

  // 4. Draw them strictly in the correct order
  for (let i = 0; i < drawTasks.length; i++) {
    const img = loadedImages[i]
    if (img) {
      drawTasks[i].draw(img)
    }
  }

  // Background is always filled first above, so the final canvas is fully
  // opaque, JPEG is safe here regardless of the format of any individual
  // slot photo (transparent pose photos just reveal the background beneath them).
  return canvas.toDataURL('image/jpeg', 0.92)
}
